import { z } from "zod";

export const getZodSchema = (
  required = [],
  visibleFields = [],
  deliveryMethod = {},
  hasFee,
  language
) => {
  const { mandatoryAddress, mandatoryCellPhone } = deliveryMethod;

  const noValidationNeeded = (field, value) => {
    // No validation is needed if the field is not required and empty, or if the field is not visible
    return (
      (!required.includes(field) && value === "") ||
      !visibleFields.includes(field)
    );
  };

  return z.object({
    firstName: z
      .string()
      .min(1, {
        message: `${language.Responsible_FirstName} ${language.Responsible_IsRequired}`,
      })
      .max(100, { message: language.Responsible_InvalidLength }),
    lastName: z
      .string()
      .min(1, {
        message: `${language.Responsible_LastName} ${language.Responsible_IsRequired}`,
      })
      .max(100, { message: language.Responsible_InvalidLength }),
    civicRegistrationNumber: z
      .string()
      .trim()
      .refine(
        (value) =>
          noValidationNeeded("civicRegistrationNumber", value) ||
          /^([1-2][0|9]){0,1}[0-9]{2}[0-1][0-9][0-3][0-9][-]{0,1}[0-9]{4}$/.test(
            value
          ),
        {
          message: language.Responsible_Invalid_CivicRegistrationNumber,
        }
      )
      .refine(
        (value) =>
          noValidationNeeded("civicRegistrationNumber", value) ||
          (value.replace("-", "").length >= 10 &&
            value.replace("-", "").length <= 12),
        {
          message: language.Responsible_CivicRegistrationNumber_Info,
        }
      ),
    email: z
      .string()
      .email({
        message: language.Responsible_Invalid_Email,
      })
      .min(1, {
        message: `${language.Responsible_Email} ${language.Responsible_IsRequired}`,
      })
      .max(100, { message: language.Responsible_InvalidLength }),
    cellPhone: z
      .string()
      .max(15, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryCellPhone && noValidationNeeded("cellPhone", value)) ||
          /^(\+(\d{1,3}\s*)|)\d{1,4}(-|\/|\s|)(\d\s*){5,10}$/.test(value),
        {
          message: language.Responsible_CellPhone_Info,
        }
      )
      .refine(
        (value) =>
          (!mandatoryCellPhone && noValidationNeeded("cellPhone", value)) ||
          value.length > 9,
        {
          message: language.Responsible_CellPhone_Info,
        }
      ),
    street: z
      .string()
      .max(100, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryAddress &&
            !hasFee &&
            noValidationNeeded("street", value)) ||
          value.length > 0,
        {
          message: `${language.Responsible_AddressStreet} ${language.Responsible_IsRequired}`,
        }
      ),
    streetNumber: z
      .string()
      .max(100, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryAddress &&
            !hasFee &&
            noValidationNeeded("streetNumber", value)) ||
          value.length > 0,
        {
          message: `${language.Responsible_AddressStreetNumber} ${language.Responsible_IsRequired}`,
        }
      ),
    zipCode: z
      .string()
      .max(20, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryAddress &&
            !hasFee &&
            noValidationNeeded("zipCode", value)) ||
          /^[0-9]{3}(\s?){0,1}[0-9]{2,}$/.test(value),
        {
          message: language.Responsible_AddressZip_Info,
        }
      ),
    city: z
      .string()
      .max(100, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryAddress && !hasFee && noValidationNeeded("city", value)) ||
          value.length > 0,
        {
          message: `${language.Responsible_AddressCity} ${language.Responsible_IsRequired}`,
        }
      ),
    countryCode: z
      .string()
      .max(100, { message: language.Responsible_InvalidLength })
      .refine(
        (value) =>
          (!mandatoryAddress &&
            !hasFee &&
            noValidationNeeded("countryCode", value)) ||
          value.length > 0,
        {
          message: `${language.Responsible_Country} ${language.Responsible_IsRequired}`,
        }
      ),
  });
};
